import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Add, Remove, Close } from "@material-ui/icons";
import { FiPackage, FiTruck, FiSettings, FiShoppingCart, FiPlus, FiMinus, FiInfo, FiCheckCircle, FiXCircle } from "react-icons/fi";
import StepCodeTag from "../../../components/shared/StepCodeTag";
import { PURCHASE_ORDER_STEP_CODES } from "../../../constants/workflowStepCodes";

const LabelWithTag = ({ code, children }) => (
    <label className="inline-flex items-center gap-2 flex-wrap">
        {code && <StepCodeTag code={code} />}
        {children}
    </label>
);

const CellWithTag = ({ code, children }) => (
    <div className="inline-flex items-center gap-2 flex-wrap">
        {code && <StepCodeTag code={code} />}
        {children}
    </div>
);

const PurchaseOrderTab = ({
    materialsCart,
    inputs,
    handleChange,
    handleMaterialQuantity,
    handleMaterialPrice,
    handleMaterialClassificationCode,
    handleMaterialCompany,
    handleMaterialMeasurements,
    handleAddCustomAddOnMaterial,
    handlePosCreatePurchaseOrder,
    handleCreatePurchaseOrderFromSelection,
    cashierMode,
    setCashierMode,
    setFilterList,
    products,
    setColor,
    setProduct,
    setItemDetails,
    setColorPicker,
    orderData,
    handleSubmitPurchaseOrder,
    handleApprovePurchaseOrder,
    handleRejectPurchaseOrder,
    handleConfirmPOArrived,
    superadmin,
    manager,
    outright,
    production,
    artist,
    companiesData,
    currentUser,
    purchaser,
}) => {
    const history = useHistory();
    const [selectMaterialsModalOpen, setSelectMaterialsModalOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth <= 768);
    const [selectedIndices, setSelectedIndices] = useState(new Set());

    const todayDateString = () => {
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    };

    const [generatePOModalOpen, setGeneratePOModalOpen] = useState(false);
    const [generatePOForm, setGeneratePOForm] = useState({
        dateOfIssue: todayDateString(),
        poNumber: "",
        remarks: "",
    });
    const [isSubmittingPO, setIsSubmittingPO] = useState(false);
    const [pendingPOIndices, setPendingPOIndices] = useState([]);

    const [addOnModalOpen, setAddOnModalOpen] = useState(false);
    const [addOnForm, setAddOnForm] = useState({ name: "", quantity: "1", price: "0" });
    const [addOnSourceProduct, setAddOnSourceProduct] = useState(null);

    const AUTOCOUNT_PRINCIPLE_ID = "63429be7a4b0715d61b8955c";
    const canGeneratePO =
        String(currentUser?._id) === AUTOCOUNT_PRINCIPLE_ID ||
        String(currentUser?.principle) === AUTOCOUNT_PRINCIPLE_ID;

    const isAddOnTitle = (title) => {
        const t = (title || "").trim().toLowerCase();
        return t === "add on" || t === "add-on" || t === "addon";
    };

    const openAddOnModal = (product) => {
        setAddOnSourceProduct(product || null);
        setAddOnForm({ name: "", quantity: "1", price: "0" });
        setAddOnModalOpen(true);
        if (isMobile) setSelectMaterialsModalOpen(false);
    };

    const closeAddOnModal = () => {
        setAddOnModalOpen(false);
        setAddOnForm({ name: "", quantity: "1", price: "0" });
        setAddOnSourceProduct(null);
    };

    const handleSubmitAddOn = () => {
        const trimmedName = addOnForm.name.trim();
        if (!trimmedName) {
            alert("Please enter an item name for the Add On.");
            return;
        }
        const parsedQuantity = parseInt(addOnForm.quantity, 10);
        const quantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
        const parsedPrice = parseFloat(addOnForm.price);
        const price = Number.isFinite(parsedPrice) && parsedPrice >= 0 ? parsedPrice : 0;

        if (handleAddCustomAddOnMaterial) {
            handleAddCustomAddOnMaterial(trimmedName, quantity, price, addOnSourceProduct);
        }
        closeAddOnModal();
    };

    const toggleMaterialSelection = (index) => {
        const material = materialsCart[index];
        if (!material) return;
        const canSelect = material.company && !material.purchaseOrderId;
        if (!canSelect) return;
        setSelectedIndices((prev) => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    const openGeneratePOModal = () => {
        if (!handleCreatePurchaseOrderFromSelection) return;
        if (selectedIndices.size === 0) {
            alert("Please select at least one material to create a purchase order.");
            return;
        }
        setPendingPOIndices([...selectedIndices]);
        setGeneratePOForm({
            dateOfIssue: todayDateString(),
            poNumber: "",
            remarks: "",
        });
        setGeneratePOModalOpen(true);
    };

    const closeGeneratePOModal = () => {
        if (isSubmittingPO) return;
        setGeneratePOModalOpen(false);
        setPendingPOIndices([]);
    };

    const handleSubmitGeneratePO = async () => {
        if (!handleCreatePurchaseOrderFromSelection) return;
        if (pendingPOIndices.length === 0) {
            closeGeneratePOModal();
            return;
        }
        const indices = pendingPOIndices;
        const extras = {
            dateOfIssue: generatePOForm.dateOfIssue || todayDateString(),
            poNumber: generatePOForm.poNumber,
            remarks: generatePOForm.remarks,
        };
        setIsSubmittingPO(true);
        try {
            const result = await handleCreatePurchaseOrderFromSelection(indices, extras);
            if (result?.autocountFailed) {
                alert("Creating PO on Autocount failed. Contact admin for support");
            }
            setSelectedIndices(new Set());
            setGeneratePOModalOpen(false);
            setPendingPOIndices([]);
        } finally {
            setIsSubmittingPO(false);
        }
    };

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const selectMaterialsContent = (
        <>
            <div className="checkoutButtons">
                <div
                    className="salesButton bronze"
                    onClick={() => setFilterList({ type: "watch" })}
                >
                    Select Materials
                </div>
            </div>
            <div className="posContainer">
                {products ? (
                    products.filter(product => {
                        const normalizedTitle = (product.title || "").trim().toLowerCase();
                        const excludedTitles = [
                            "acp",
                            "glass",
                            "metal deck",
                            "polycarbonate",
                            "awning",
                            "others",
                            "other",
                        ];
                        return !excludedTitles.includes(normalizedTitle);
                    }).map((product, i) => (
                        <div
                            key={i}
                            className="posItem"
                            onClick={() => {
                                if (isAddOnTitle(product.title)) {
                                    openAddOnModal(product);
                                    return;
                                }
                                setColor(product.color);
                                setItemDetails({
                                    name: `${product.title}`,
                                    isMaterial: true,
                                });
                                window.scrollTo(0, 0);
                                setColorPicker(true);
                                if (isMobile) setSelectMaterialsModalOpen(false);
                            }}
                        >
                            <StepCodeTag code={PURCHASE_ORDER_STEP_CODES.materialsAndAddOn} />
                            <div className="productDetails">
                                {product.title}
                            </div>
                        </div>
                    ))
                ) : (
                    <></>
                )}
            </div>
        </>
    );

    return (
        <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
            {/* Enhanced Header Section */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">
                            Purchase Order Management
                        </h1>
                        <p className="text-gray-600 text-sm mb-3">
                            Manage materials, submit purchase orders, and track production workflow
                        </p>
                        {handleCreatePurchaseOrderFromSelection && canGeneratePO && (purchaser || superadmin || manager || outright) && !production && (
                            <button
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                                    selectedIndices.size > 0
                                        ? "bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white shadow-md hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-orange-300"
                                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                }`}
                                onClick={() => {
                                    if (selectedIndices.size > 0) openGeneratePOModal();
                                }}
                                disabled={selectedIndices.size === 0}
                            >
                                <StepCodeTag code={PURCHASE_ORDER_STEP_CODES.generatePO} />
                                <FiPlus className="h-4 w-4" />
                                Generate PO
                            </button>
                        )}
                    </div>
                    
                    {/* Enhanced Action Buttons */}
                    <div className="flex flex-wrap gap-3">
                        {(purchaser || superadmin || manager || outright) && !production && ["uploadedDraft", "draftApproved"].includes(orderData?.status) && (
                            <>
                            <button
                                className="group flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-xl shadow-lg hover:shadow-xl hover:from-orange-700 hover:to-orange-800 focus:outline-none focus:ring-4 focus:ring-orange-300 transform hover:scale-105 transition-all duration-200"
                                onClick={handleSubmitPurchaseOrder}
                            >
                                <StepCodeTag code={PURCHASE_ORDER_STEP_CODES.submitPurchaseOrder} />
                                <FiPackage className="h-5 w-5 group-hover:animate-bounce" />
                                <span className="font-semibold">Submit Purchase Order</span>
                            </button>

                            <button
                                className="group flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl shadow-lg hover:shadow-xl hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-4 focus:ring-red-300 transform hover:scale-105 transition-all duration-200"
                                onClick={handleRejectPurchaseOrder}
                            >
                                <FiXCircle className="h-5 w-5 group-hover:animate-pulse" />
                                <span className="font-semibold">Reject Purchase Order</span>
                            </button>
                            </>
                        )}
                        
                        {(superadmin || manager || outright) && !production && orderData?.status === "pendingPurchaseApproval" && (
                            <>
                                <button
                                    className="group flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl shadow-lg hover:shadow-xl hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-4 focus:ring-green-300 transform hover:scale-105 transition-all duration-200"
                                    onClick={handleApprovePurchaseOrder}
                                >
                                    <FiCheckCircle className="mr-2 h-5 w-5 group-hover:animate-pulse" />
                                    <span className="font-semibold">Approve Purchase Order</span>
                                </button>

                            </>
                        )}

                        {(purchaser || superadmin || manager || outright) && orderData?.status === "purchasing" && (
                            <button
                                className="group flex items-center px-6 py-3 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl shadow-lg hover:shadow-xl hover:from-teal-700 hover:to-teal-800 focus:outline-none focus:ring-4 focus:ring-teal-300 transform hover:scale-105 transition-all duration-200"
                                onClick={handleConfirmPOArrived}
                            >
                                <FiTruck className="mr-2 h-5 w-5 group-hover:animate-bounce" />
                                <span className="font-semibold">Confirm PO Arrived</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
            <div className="pos">
                <div className="userContainer">
                    <div className="checkoutButtons">
                        <div
                            className="salesButton bronze"
                            onClick={() => setCashierMode("purchase")}
                        >
                            Purchase Order
                        </div>
                    </div>
                    <div className="w-full overflow-x-auto">
                        <table className="w-full min-w-max border-collapse">
                            <thead>
                                <tr className="bg-gradient-to-r from-gray-800 to-gray-900 text-white">
                                    <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider border-r border-gray-700 w-12">Select</th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-gray-700">Item Code</th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-gray-700">Item Name</th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-gray-700 w-32">Price</th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-gray-700">Measurements</th>
                                    <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider border-r border-gray-700">Quantity</th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-gray-700">Classification Code</th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-gray-700">Company (Supplier)</th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider w-40">PO Number</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {materialsCart && materialsCart.length > 0 ? (
                                    materialsCart.map((material, i) => {
                                        const canSelect = material.company && !material.purchaseOrderId;
                                        const isSelected = selectedIndices.has(i);
                                        return (
                                        <tr 
                                            key={material.uniqueId || i}
                                            className={`transition-colors duration-150 ${material.record === "late" ? "bg-yellow-200 hover:bg-yellow-300" : material.record === "normal" ? "bg-green-200 hover:bg-green-300" : "hover:bg-gray-50"} ${material.purchaseOrderId ? "opacity-75" : ""}`}
                                        >
                                            {/* Select checkbox */}
                                            <td className="px-3 py-3 text-center border-r border-gray-200">
                                                <CellWithTag code={PURCHASE_ORDER_STEP_CODES.select}>
                                                    {material.purchaseOrderId ? (
                                                        <span className="text-xs text-gray-500" title="Already in purchase order">—</span>
                                                    ) : (
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => toggleMaterialSelection(i)}
                                                            disabled={!canSelect}
                                                            title={!material.company ? "Assign a company first" : "Select for purchase order"}
                                                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        />
                                                    )}
                                                </CellWithTag>
                                            </td>
                                            {/* Item Code */}
                                            <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap border-r border-gray-200">
                                                {material.itemCode || material.color?.itemCode || material.activeColor?.itemCode || material.code || 'N/A'}
                                            </td>
                                            
                                            {/* Item Name */}
                                            <td className="px-3 py-3 text-sm font-medium text-gray-900 capitalize whitespace-nowrap border-r border-gray-200">
                                                {material.activeColor?.type || 'N/A'}
                                            </td>
                                            
                                            {/* Price */}
                                            <td className="px-3 py-3 border-r border-gray-200">
                                                {!production && !material.purchaseOrderId && !material.purchaseOrderPoNumber ? (
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-sm font-medium text-gray-600">RM</span>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={material.price || 0}
                                                            onChange={(e) => {
                                                                const newPrice = parseFloat(e.target.value) || 0;
                                                                handleMaterialPrice(material, newPrice);
                                                            }}
                                                            className="w-24 px-2 py-1.5 text-sm text-center border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-medium transition-all"
                                                        />
                                                    </div>
                                                ) : (
                                                    <span className="text-sm font-medium text-gray-900">
                                                        RM {material.price || material.activeColor?.price || 0}
                                                    </span>
                                                )}
                                            </td>
                                            
                                            {/* Measurements */}
                                            <td className="px-3 py-3 border-r border-gray-200">
                                                <CellWithTag code={PURCHASE_ORDER_STEP_CODES.measurements}>
                                                    {!production && !material.purchaseOrderId && !material.purchaseOrderPoNumber ? (
                                                        <input
                                                            type="text"
                                                            value={material.measurements ?? ""}
                                                            placeholder="e.g. 2m x 1.5m"
                                                            onChange={(e) => {
                                                                if (handleMaterialMeasurements) {
                                                                    handleMaterialMeasurements(material, e.target.value);
                                                                }
                                                            }}
                                                            className="w-40 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-medium"
                                                        />
                                                    ) : (
                                                        <span className="text-sm text-gray-900">
                                                            {material.measurements?.trim()
                                                                ? material.measurements
                                                                : "N/A"}
                                                        </span>
                                                    )}
                                                </CellWithTag>
                                            </td>

                                            {/* Quantity */}
                                            <td className="px-3 py-3 border-r border-gray-200">
                                                <div className="flex items-center justify-center gap-2">
                                                    {!production && !material.purchaseOrderId && !material.purchaseOrderPoNumber ? (
                                                        <>
                                                            <button
                                                                onClick={() => handleMaterialQuantity(material, -1, Math.max(0, material.amount - 1))}
                                                                className="w-8 h-8 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-sm"
                                                            >
                                                                <FiMinus />
                                                            </button>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                value={material.amount}
                                                                onChange={(e) => {
                                                                    const newAmount = parseInt(e.target.value) || 0;
                                                                    handleMaterialQuantity(material, newAmount - material.amount, newAmount);
                                                                }}
                                                                className="w-16 px-2 py-1.5 text-sm text-center border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-medium"
                                                            />
                                                            <button
                                                                onClick={() => handleMaterialQuantity(material, 1, material.amount + 1)}
                                                                className="w-8 h-8 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors shadow-sm"
                                                            >
                                                                <FiPlus />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <span className="text-sm font-medium text-gray-900">{material.amount}</span>
                                                    )}
                                                </div>
                                            </td>
                                            
                                            {/* Classification Code */}
                                            <td className="px-3 py-3 border-r border-gray-200">
                                                {!production && !material.purchaseOrderId && !material.purchaseOrderPoNumber ? (
                                                    <input
                                                        type="text"
                                                        data-no-capitalize
                                                        value={material.classificationCode ?? orderData?.coNumber ?? ""}
                                                        onChange={(e) => {
                                                            if (handleMaterialClassificationCode) {
                                                                handleMaterialClassificationCode(material, e.target.value);
                                                            }
                                                        }}
                                                        className="w-40 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-medium"
                                                    />
                                                ) : (
                                                    <span className="text-sm text-gray-900">
                                                        {material.classificationCode || orderData?.coNumber || "N/A"}
                                                    </span>
                                                )}
                                            </td>

                                            {/* Company (Supplier) */}
                                            <td className="px-3 py-3 border-r border-gray-200">
                                                <CellWithTag code={PURCHASE_ORDER_STEP_CODES.companySupplier}>
                                                    {!production && !material.purchaseOrderId && !material.purchaseOrderPoNumber ? (
                                                        <select
                                                            className="w-48 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-medium bg-white"
                                                            value={material.company || ""}
                                                            onChange={(e) => {
                                                                if (handleMaterialCompany) {
                                                                    handleMaterialCompany(material, e.target.value || "");
                                                                }
                                                            }}
                                                        >
                                                            <option value="">Select Company</option>
                                                            {Array.isArray(companiesData) &&
                                                                companiesData
                                                                    .filter((company) => {
                                                                        if (currentUser?.isSuperAdmin) return true;
                                                                        const userPrinciple = currentUser?.type === "outright" 
                                                                            ? currentUser?._id 
                                                                            : currentUser?.principle?._id || currentUser?.principle;
                                                                        const companyPrinciple = company.principle?._id || company.principle;
                                                                        return !userPrinciple || companyPrinciple === userPrinciple;
                                                                    })
                                                                    .map((company) => (
                                                                        <option key={company._id} value={company._id}>
                                                                            {company.name}
                                                                        </option>
                                                                    ))}
                                                        </select>
                                                    ) : (
                                                        <span className="text-sm text-gray-900">
                                                            {material.companyName ||
                                                                (Array.isArray(companiesData)
                                                                    ? (companiesData.find((c) => c._id === material.company)?.name || "N/A")
                                                                    : "N/A")}
                                                        </span>
                                                    )}
                                                </CellWithTag>
                                            </td>

                                            {/* PO Number */}
                                            <td className="px-3 py-3 w-40">
                                                {material.purchaseOrderPoNumber ? (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const poDisplay = material.purchaseOrderPoNumber.replace(/\s*\([^)]+\)\s*$/, "");
                                                            history.push(`/purchaseOrders?poNumber=${encodeURIComponent(poDisplay)}`);
                                                        }}
                                                        className="text-sm font-medium text-slate-700 bg-slate-100 rounded px-2 py-1 hover:bg-slate-200 hover:text-slate-900 cursor-pointer transition-colors text-left"
                                                    >
                                                        {material.purchaseOrderPoNumber.replace(/\s*\([^)]+\)\s*$/, "")}
                                                    </button>
                                                ) : (
                                                    <span className="text-sm text-gray-400">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ); })
                                ) : (
                                    <tr>
                                        <td colSpan={9} className="px-3 py-8 text-center text-sm text-gray-500">
                                            No materials added yet
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="summary w-full flex lightblue">
                        <div className="summarysubtotal">Total Materials</div>
                        <div className="summarysubtotal">
                            {materialsCart
                                ? materialsCart.reduce(
                                      (total, item) => total + item.amount,
                                      0
                                  )
                                : 0}
                        </div>
                    </div>

                    {!production && (
                        <div className="checkoutButtons flex flex-wrap gap-3">
                            <div
                                className={`salesButton sales ${
                                    orderData?.status === "completed"
                                        ? "disabled"
                                        : ""
                                }`}
                                onClick={() => {
                                    if (orderData?.status !== "completed") {
                                        handlePosCreatePurchaseOrder();
                                    } else {
                                        alert(
                                            "Update Purchase Order is not available for completed orders."
                                        );
                                    }
                                }}
                            >
                                Update Purchase Order
                            </div>
                        </div>
                    )}
                </div>

                {/* Desktop: show Select Materials section inline. Mobile: show icon that opens modal */}
                <div className={`posWrapper lightred purchase-order-select-materials-section ${isMobile ? "purchase-order-select-materials-hidden" : ""}`}>
                    {selectMaterialsContent}
                </div>
                {isMobile && (
                    <>
                        <button
                            type="button"
                            className="purchase-order-select-materials-trigger"
                            onClick={() => setSelectMaterialsModalOpen(true)}
                            aria-label="Select materials"
                        >
                            <FiShoppingCart className="purchase-order-select-materials-trigger-icon" />
                            <span>Select Materials</span>
                        </button>
                        {selectMaterialsModalOpen && (
                            <>
                                <div
                                    className="purchase-order-select-materials-backdrop"
                                    onClick={() => setSelectMaterialsModalOpen(false)}
                                    aria-hidden="true"
                                />
                                <div className="purchase-order-select-materials-modal">
                                    <div className="purchase-order-select-materials-modal-header">
                                        <h3>Select Materials</h3>
                                        <button
                                            type="button"
                                            className="purchase-order-select-materials-modal-close"
                                            onClick={() => setSelectMaterialsModalOpen(false)}
                                            aria-label="Close"
                                        >
                                            <Close />
                                        </button>
                                    </div>
                                    <div className="purchase-order-select-materials-modal-body">
                                        {selectMaterialsContent}
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            {addOnModalOpen && (
                <div className="product-picker-modal-root" role="dialog" aria-modal="true">
                    <div
                        className="product-picker-backdrop"
                        style={{
                            position: "fixed",
                            inset: 0,
                            background: "black",
                            opacity: 0.75,
                            zIndex: 10,
                        }}
                        onClick={closeAddOnModal}
                        aria-hidden="true"
                    />
                    <div
                        className="product-picker-overlay"
                        style={{
                            position: "fixed",
                            inset: 0,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: 11,
                            pointerEvents: "none",
                        }}
                    >
                        <div
                            onClick={closeAddOnModal}
                            style={{
                                height: "100%",
                                width: "50%",
                                pointerEvents: "auto",
                            }}
                            className="add-on-empty-side"
                        />
                        <div
                            className="product-picker-panel"
                            style={{
                                height: "100%",
                                width: "50%",
                                display: "flex",
                                background: "white",
                                pointerEvents: "auto",
                            }}
                        >
                            <div
                                className="product-picker-panel-inner"
                                style={{
                                    background: "white",
                                    height: "100%",
                                    width: "100%",
                                    display: "grid",
                                }}
                            >
                                <div className="purchase-order-select-materials-modal-header product-picker-mobile-header">
                                    <h3>Add On</h3>
                                    <button
                                        type="button"
                                        className="purchase-order-select-materials-modal-close"
                                        onClick={closeAddOnModal}
                                        aria-label="Close"
                                    >
                                        <Close />
                                    </button>
                                </div>
                                <div
                                    className="product-picker-modal-body"
                                    style={{
                                        overflowY: "auto",
                                        overflowX: "hidden",
                                        height: "100vh",
                                        background: "rgb(240, 240, 255)",
                                    }}
                                >
                                    <div className="itemDetailsContainer">
                                        <button
                                            type="button"
                                            className="close-menu-button"
                                            onClick={closeAddOnModal}
                                            aria-label="Close"
                                        >
                                            <Close />
                                        </button>

                                        <div className="summary">
                                            <div className="addProductItem fullWidth">
                                                <LabelWithTag code={PURCHASE_ORDER_STEP_CODES.itemCodeAndName}>
                                                    Item Name
                                                </LabelWithTag>
                                                <input
                                                    name="addOnName"
                                                    type="text"
                                                    autoFocus
                                                    placeholder="Enter item name"
                                                    value={addOnForm.name}
                                                    onChange={(e) =>
                                                        setAddOnForm((prev) => ({
                                                            ...prev,
                                                            name: e.target.value,
                                                        }))
                                                    }
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            handleSubmitAddOn();
                                                        }
                                                    }}
                                                />
                                            </div>

                                            <div className="addProductItem fullWidth">
                                                <LabelWithTag code={PURCHASE_ORDER_STEP_CODES.quantity}>
                                                    Quantity
                                                </LabelWithTag>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "10px",
                                                    }}
                                                >
                                                    <Remove
                                                        style={{
                                                            fontSize: "1.2rem",
                                                            cursor: "pointer",
                                                            padding: "4px",
                                                            borderRadius: "4px",
                                                            border: "1px solid #d1d5db",
                                                            backgroundColor: "#f9fafb",
                                                        }}
                                                        onClick={() => {
                                                            const current =
                                                                parseInt(addOnForm.quantity, 10) || 1;
                                                            if (current > 1) {
                                                                setAddOnForm((prev) => ({
                                                                    ...prev,
                                                                    quantity: String(current - 1),
                                                                }));
                                                            }
                                                        }}
                                                    />
                                                    <input
                                                        name="quantity"
                                                        type="number"
                                                        min="1"
                                                        placeholder="Quantity"
                                                        value={addOnForm.quantity}
                                                        onChange={(e) => {
                                                            const value =
                                                                parseInt(e.target.value, 10) || 1;
                                                            setAddOnForm((prev) => ({
                                                                ...prev,
                                                                quantity: String(Math.max(1, value)),
                                                            }));
                                                        }}
                                                        style={{
                                                            flex: 1,
                                                            textAlign: "center",
                                                            padding: "8px",
                                                        }}
                                                    />
                                                    <Add
                                                        style={{
                                                            fontSize: "1.2rem",
                                                            cursor: "pointer",
                                                            padding: "4px",
                                                            borderRadius: "4px",
                                                            border: "1px solid #d1d5db",
                                                            backgroundColor: "#f9fafb",
                                                        }}
                                                        onClick={() => {
                                                            const current =
                                                                parseInt(addOnForm.quantity, 10) || 1;
                                                            setAddOnForm((prev) => ({
                                                                ...prev,
                                                                quantity: String(current + 1),
                                                            }));
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            <div className="addProductItem fullWidth">
                                                <LabelWithTag code={PURCHASE_ORDER_STEP_CODES.price}>
                                                    Price
                                                </LabelWithTag>
                                                <input
                                                    name="price"
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    placeholder="Enter price"
                                                    value={addOnForm.price}
                                                    onChange={(e) =>
                                                        setAddOnForm((prev) => ({
                                                            ...prev,
                                                            price: e.target.value,
                                                        }))
                                                    }
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            handleSubmitAddOn();
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            className="productListEdit mt-2"
                                            onClick={handleSubmitAddOn}
                                        >
                                            <span className="inline-flex items-center gap-2">
                                                <StepCodeTag code={PURCHASE_ORDER_STEP_CODES.addToMaterials} />
                                                Add To Materials
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {generatePOModalOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="generate-po-modal-title"
                >
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={closeGeneratePOModal}
                        aria-hidden="true"
                    />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                            <h3
                                id="generate-po-modal-title"
                                className="text-lg font-semibold text-gray-900"
                            >
                                Generate Purchase Order
                            </h3>
                            <button
                                type="button"
                                onClick={closeGeneratePOModal}
                                disabled={isSubmittingPO}
                                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                                aria-label="Close"
                            >
                                <Close />
                            </button>
                        </div>
                        <div className="px-6 py-5 space-y-4">
                            <div>
                                <label
                                    htmlFor="generate-po-date"
                                    className="block text-sm font-medium text-gray-700 mb-1"
                                >
                                    Date
                                </label>
                                <input
                                    id="generate-po-date"
                                    type="date"
                                    value={generatePOForm.dateOfIssue}
                                    onChange={(e) =>
                                        setGeneratePOForm((prev) => ({
                                            ...prev,
                                            dateOfIssue: e.target.value,
                                        }))
                                    }
                                    disabled={isSubmittingPO}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Defaults to today if left empty.
                                </p>
                            </div>

                            <div>
                                <label
                                    htmlFor="generate-po-number"
                                    className="block text-sm font-medium text-gray-700 mb-1"
                                >
                                    PO Number{" "}
                                    <span className="text-gray-400 font-normal">(optional)</span>
                                </label>
                                <input
                                    id="generate-po-number"
                                    type="text"
                                    data-no-capitalize
                                    value={generatePOForm.poNumber}
                                    onChange={(e) =>
                                        setGeneratePOForm((prev) => ({
                                            ...prev,
                                            poNumber: e.target.value,
                                        }))
                                    }
                                    disabled={isSubmittingPO}
                                    placeholder="Leave blank to auto-generate"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="generate-po-remarks"
                                    className="block text-sm font-medium text-gray-700 mb-1"
                                >
                                    Remarks{" "}
                                    <span className="text-gray-400 font-normal">(optional)</span>
                                </label>
                                <textarea
                                    id="generate-po-remarks"
                                    value={generatePOForm.remarks}
                                    onChange={(e) =>
                                        setGeneratePOForm((prev) => ({
                                            ...prev,
                                            remarks: e.target.value,
                                        }))
                                    }
                                    disabled={isSubmittingPO}
                                    rows={3}
                                    placeholder="Any notes for this purchase order"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-y"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                            <button
                                type="button"
                                onClick={closeGeneratePOModal}
                                disabled={isSubmittingPO}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmitGeneratePO}
                                disabled={isSubmittingPO}
                                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 rounded-lg shadow disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {isSubmittingPO ? "Creating…" : "Create Purchase Order"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PurchaseOrderTab;
